import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './OrganizationImageBlockEditPage.module.css';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from '../../atoms/Spinner/Spinner';
import { ImageBlockResponseDto } from '../../../store/buyerAPI/block/dto/response/image-block-response.dto';
import {
  fetchBlocks,
  fetchImageBlockById,
  updateImageBlock,
} from '../../../store/buyerAPI/block/reducers/block.slice';
import { ImageBlockUpdateRequestDto } from '../../../store/buyerAPI/block/dto/request/image-block-update-request.dto';
import { OrganizationImageBlockEditTemplate } from '../../templates';

interface OrganizationImageBlockEditPageProps {}

const OrganizationImageBlockEditPage: FC<
  OrganizationImageBlockEditPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { organizationId, blockId } = useParams();
  const navigate = useNavigate();

  const [imageBlock, setImageBlock] = useState<ImageBlockResponseDto>();
  const [imageMeta, setImageMeta] = useState<ImageMeta>();
  const [backgroundImageMeta, setBackgroundImageMeta] = useState<ImageMeta>();

  const { imageBlockStatus: status } = useSelector(
    (state: RootState) => state.blockSlice
  );
  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  useEffect(() => {
    if (blockId) {
      dispatch(fetchImageBlockById(blockId))
        .unwrap()
        .then((resp) => setImageBlock(resp));
    }
  }, [dispatch, blockId]);

  const handleOnChange = (imageBlock: ImageBlockResponseDto) => {
    setImageBlock(imageBlock);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const handleBackgroundOnChange = (imageMeta: ImageMeta) => {
    setBackgroundImageMeta(imageMeta);
  };

  const saveImage = async (
    imageUrl: string | undefined,
    imageMeta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (imageUrl && imageUrl !== '' && imageMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([imageUrl], { type: imageMeta.imageType ?? 'image/png' }),
        imageMeta.imageSrc
      );
      return (await dispatch(saveMedia(formData)))
        .payload as MediaSaveResponseDto;
    }
    return { href: imageUrl ?? '' };
  };

  const callbackFn = async (imageBlock: ImageBlockResponseDto) => {
    const image = await saveImage(imageBlock.imageUrl ?? '', imageMeta);
    const backgroundImage = await saveImage(
      imageBlock.backgroundImageUrl ?? '',
      backgroundImageMeta
    );
    const imageBlockToUpdate: ImageBlockUpdateRequestDto = {
      imageUrl: image.href,
      backgroundImageUrl: backgroundImage.href,
      backgroundColor: imageBlock.backgroundColor,
      imageAltText: imageBlock.imageAltText,
      withTexture: imageBlock.withTexture,
      header: imageBlock.header,
      imageSize: imageBlock.imageSize,
      blockType: imageBlock.blockType,
      organizationId: imageBlock.organizationId,
      id: imageBlock.id,
      published: imageBlock.published,
    };
    await dispatch(updateImageBlock(imageBlockToUpdate));
    dispatch(fetchBlocks(organizationId!));
    navigate(`/organizations/edit/${organizationId}/content-blocks`);
  };

  if (status === 'loading' || mediaStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <>
      {imageBlock && (
        <div className={cn(styles.wrapper)}>
          <OrganizationImageBlockEditTemplate
            handleOnChange={handleOnChange}
            handleImageOnChange={handleImageOnChange}
            handleBackgroundOnChange={handleBackgroundOnChange}
            imageBlock={imageBlock}
            callbackFn={callbackFn}
          />
        </div>
      )}
    </>
  );
};

export default OrganizationImageBlockEditPage;
